import { Sprite, Texture, type Container } from 'pixi.js';
import { BossKind, GameMode } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import { BOSS_SLUGS } from '../assets/registry';
import type { StackRegistry } from '../assets/StackRegistry';
import { WORLD_TO_R2, placeSprite } from '../scale';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

/** Rendered frames the boss's transient states are held for. */
const HIT_FRAMES = 6;
const PHASE_FRAMES = 12;
const ENTER_FRAMES = 30;
const DYING_FRAMES = 18;
/** Ticks after an attack timer reset that still read as "just fired". */
const ATTACK_TICKS = 8;
const TELEGRAPH_LINE_STATE = 1;
const LINE_COLOR = 0xff3f9b;
const LINE_WIDTH = 2;
const LINE_BASE_ALPHA = 0.3;
const LINE_BLINK_ALPHA = 0.4;
const LINE_BLINK_SHIFT = 2;
const MIN_LINE_LENGTH = 1;
/** Matches BossSystem.lineState / lineX1 / lineY1 / lineX2 / lineY2 fixed length (8). */
const TELEGRAPH_LINE_CAPACITY = 8;

const STATE_DYING = 'dying';
const STATE_PHASE = 'phase-transition';
const STATE_HIT = 'hit';
const STATE_TELEGRAPH = 'telegraph';
const STATE_ATTACK = 'attack';
const STATE_ENTER = 'enter';
const STATE_NEUTRAL = 'neutral';

/**
 * One sprite for the whole boss, anchored on its core part. Individual parts
 * get no sprite of their own in v1 — the authored bosses are single figures,
 * not part assemblies, so drawing 32 copies of the figure would be wrong.
 * The telegraph lines DO get drawn: they are the fight's readable warning.
 *
 * Every transient pose (enter, hit, phase, death) is renderer-local state and
 * lives here with the sprites it drives.
 */
export class BossFamily implements EntityFamily {
  private stacks: StackRegistry | null = null;
  private layer: Container | null = null;
  private mobilePerformanceMode = false;
  private readonly sprites: (LayerStackSprite | null)[] = [];
  private readonly telegraphSprites: Sprite[] = [];
  private shown = BossKind.None;
  private prevHp = 0;
  private prevPhase = 0;
  private enterFrames = 0;
  private hitFrames = 0;
  private phaseFrames = 0;
  private dyingFrames = 0;

  init(deps: FamilyDeps): void {
    this.stacks = deps.stacks;
    this.layer = deps.layer;
    this.mobilePerformanceMode = deps.mobilePerformanceMode;
    for (let i = 0; i < BOSS_SLUGS.length; i += 1) {
      const slug = BOSS_SLUGS[i]!;
      if (slug === '' || this.mobilePerformanceMode) {
        this.sprites.push(null);
        continue;
      }
      const sprite = deps.stacks.acquire(slug, deps.layer);
      sprite.visible = false;
      this.sprites.push(sprite);
    }
    for (let i = 0; i < TELEGRAPH_LINE_CAPACITY; i += 1) {
      const line = new Sprite(Texture.WHITE);
      line.anchor.set(0.5);
      line.roundPixels = true;
      line.visible = false;
      line.tint = LINE_COLOR;
      deps.beamLayer.addChild(line);
      this.telegraphSprites.push(line);
    }
  }

  /**
   * Dropped rather than carried across a scene change: renderer-local tails
   * (the death pose) belong to the scene that just ended, not the one starting.
   */
  onSceneReset(): void {
    this.hide();
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    const stacks = this.stacks;
    if (!stacks) return;
    const boss = world.bosses;
    // Runs on every branch below (including "no boss shown" and "defeated"),
    // not just the live-fight path: BossSystem zeroes lineState on defeat and
    // on reset, and telegraphSprites must fall to invisible in step with that,
    // the same way the old per-frame beams.clear() silently dropped them.
    const telegraphing = this.drawTelegraph(world);
    if (boss.active && boss.kind !== this.shown) {
      this.hide();
      this.shown = boss.kind;
      this.prevHp = boss.hp;
      this.prevPhase = boss.phase;
      this.enterFrames = ENTER_FRAMES;
      this.hitFrames = 0;
      this.phaseFrames = 0;
      this.dyingFrames = 0;
    }
    if (this.mobilePerformanceMode && boss.active && !this.sprites[this.shown]) {
      const slug = BOSS_SLUGS[this.shown]!;
      if (slug !== '') this.sprites[this.shown] = stacks.acquire(slug, this.layer);
    }
    const sprite = this.sprites[this.shown] ?? null;
    if (!sprite) return;
    if (!boss.active) {
      this.syncDefeated(world, sprite, stacks);
      return;
    }
    const slug = BOSS_SLUGS[this.shown]!;
    if (boss.phase !== this.prevPhase) this.phaseFrames = PHASE_FRAMES;
    else if (this.phaseFrames > 0) this.phaseFrames -= 1;
    this.prevPhase = boss.phase;
    if (boss.hp < this.prevHp) this.hitFrames = HIT_FRAMES;
    else if (this.hitFrames > 0) this.hitFrames -= 1;
    this.prevHp = boss.hp;
    if (this.enterFrames > 0) this.enterFrames -= 1;
    sprite.visible = true;
    stacks.applyState(slug, sprite, this.poseState(boss.attackTimer, telegraphing));
    const core = boss.corePart;
    const parts = world.bossParts;
    const anchored = core >= 0 && parts.indices.active[core] !== 0;
    placeSprite(sprite, anchored ? parts.x[core]! : boss.coreX, anchored ? parts.y[core]! : boss.coreY);
  }

  dispose(): void {
    this.sprites.length = 0;
    this.telegraphSprites.length = 0;
    this.layer = null;
    this.stacks = null;
  }

  /**
   * defeat() clears `active` in the same tick hp hits 0, so the death pose is a
   * renderer-side tail rather than a simulation state. BossSystem.reset() clears
   * `active` the same way on abandon/retry, and that must NOT read as a death:
   * only a boss that stopped being active mid-play has died.
   */
  private syncDefeated(world: GameWorld, sprite: LayerStackSprite, stacks: StackRegistry): void {
    if (this.dyingFrames === 0 && world.mode !== GameMode.Playing) {
      this.hide();
      return;
    }
    if (this.dyingFrames === 0) this.dyingFrames = DYING_FRAMES;
    if (this.dyingFrames > 0) {
      this.dyingFrames -= 1;
      stacks.applyState(BOSS_SLUGS[this.shown]!, sprite, STATE_DYING);
      if (this.dyingFrames === 0) this.hide();
      return;
    }
    this.hide();
  }

  private drawTelegraph(world: GameWorld): boolean {
    const boss = world.bosses;
    let telegraphing = false;
    for (let i = 0; i < this.telegraphSprites.length; i += 1) {
      const sprite = this.telegraphSprites[i]!;
      if (boss.lineState[i] !== TELEGRAPH_LINE_STATE) {
        sprite.visible = false;
        continue;
      }
      telegraphing = true;
      const x1 = boss.lineX1[i]! * WORLD_TO_R2;
      const y1 = boss.lineY1[i]! * WORLD_TO_R2;
      const x2 = boss.lineX2[i]! * WORLD_TO_R2;
      const y2 = boss.lineY2[i]! * WORLD_TO_R2;
      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.max(MIN_LINE_LENGTH, Math.hypot(dx, dy));
      sprite.visible = true;
      sprite.alpha = LINE_BASE_ALPHA + ((world.simTick >> LINE_BLINK_SHIFT) & 1) * LINE_BLINK_ALPHA;
      sprite.rotation = Math.atan2(dy, dx);
      sprite.setSize(length, LINE_WIDTH * WORLD_TO_R2);
      sprite.position.set(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2));
    }
    return telegraphing;
  }

  private poseState(attackTimer: number, telegraphing: boolean): string {
    if (this.phaseFrames > 0) return STATE_PHASE;
    if (this.hitFrames > 0) return STATE_HIT;
    if (telegraphing) return STATE_TELEGRAPH;
    if (attackTimer < ATTACK_TICKS) return STATE_ATTACK;
    return this.enterFrames > 0 ? STATE_ENTER : STATE_NEUTRAL;
  }

  private hide(): void {
    const sprite = this.sprites[this.shown] ?? null;
    if (sprite && this.mobilePerformanceMode) {
      const slug = BOSS_SLUGS[this.shown]!;
      this.stacks?.release(slug, sprite);
      this.sprites[this.shown] = null;
    } else if (sprite) {
      sprite.visible = false;
    }
    this.shown = BossKind.None;
    this.dyingFrames = 0;
  }
}
